import React, { Component, PropTypes } from 'react';
import { Tabs } from 'rmc-tabs';
import { Link } from 'react-router/es';
import { SortDirection } from "react-virtualized";

import TabBar from './TabBar';
import IngredientTable from 'components/IngredientsSearch/IngredientTable';
import {
  ingredientsSearchTabs,
  ingredientsSearchFilters,
  ingredientTabId,
  selectColumnId,
  columns,
  syscoBrandMapping,
} from 'constants/ingredientsSearch';
import {
  generateTableData,
  preventTabDefault,
  getAllowedTabList,
} from './ingredientsSearchHelper';
import ListUtilities from "records/ListUtilities";
import _ from 'lodash';
import PageLoadingCircle from 'components/PageLoading/PageLoadingCircle';
import AddIngredient from "./AddIngredient";
import { COLUMN_KEY, DIRECTION_KEY } from 'records/Sort';

const defaultSorting = {
  primaryCol: 'isSyscoBrand',
  primaryDir: SortDirection.DESC,
  secondaryCol: columns[0].key,
  secondaryDir: SortDirection.ASC,
}
class IngredientsSearchResults extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentSearchKeyword: '',
      focusOnRow: null,
      showEditableRow: false,
      [ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key]: [],
      [ingredientsSearchTabs.FULL_PRODUCT_CATALOG_TAB.key]: [],
      [ingredientsSearchTabs.ITEM_LISTS_TAB.key]: [],
      [ingredientsSearchTabs.BATCH_RECIPES_TAB.key]: [],
      [ingredientsSearchTabs.PURCHASE_HISTORY_TAB.key]: [],
      [ingredientsSearchTabs.PREVIOUSLY_USED_TAB.key]: [],
      tabHeaders: [],
      tabKeys: [],
    };
  }

  componentDidMount() {
    const {
      selectedTab,
      ingredientsSearch,
      selectedFilters,
      unitTypes,
      isBatchRecipe,
      recipeId,
      userCountry,
      isIngredientReplacement,
      isFromNotificationCenter,
    } = this.props;
    const products = ingredientsSearch[selectedTab].products || [];

    const tableData = generateTableData(products, selectedFilters, selectedTab, ingredientsSearch.search, unitTypes, isBatchRecipe, recipeId);
    const tabHeaders = getAllowedTabList(userCountry, isIngredientReplacement, isFromNotificationCenter);
    this.setState({
      [selectedTab]: tableData,
      tabHeaders,
      tabKeys: tabHeaders.map(tab => tab.key),
    });
  }

  componentWillReceiveProps(nextProps) {
    const { selectedTab, ingredientsSearch, selectedFilters } = this.props;
    const {
      selectedTab: nextSelectedTab,
      ingredientsSearch: nextIngredientsSearch,
      selectedFilters: nextSelectedFilters,
    } = nextProps;
    const products = ingredientsSearch[selectedTab].products || [];
    const nextProducts = nextIngredientsSearch[nextSelectedTab] || {};
    const nextSearch = nextIngredientsSearch.search || {};

    // conditions involed with previous state
    const tableShouldUpdate = !_.isEqual(products, nextProducts.products || []) || !_.isEqual(selectedFilters, nextSelectedFilters);
    const tabChanged = selectedTab !== nextSelectedTab;

    // data handle
    this.handleDataFlow(nextSelectedTab, nextProducts, nextSelectedFilters, nextSearch, tableShouldUpdate, tabChanged);
  }

  /**
   * handle the data flow on prop changes
   * @param {String} tab tabKey name
   * @param {Object} tabData tab data from store
   * @param {Object} filters filters from store
   * @param {Object} search search object props
   * @param {Boolean} shouldUpdate table should update
   * @param {Boolean} tabChanged tab changed
   */
  handleDataFlow = (tab, tabData, filters, search, shouldUpdate, tabChanged) => {
    const { updateSortProps, unitTypes } = this.props;
    const { currentSearchKeyword: prevKeyword } = this.state;

    const { keyword, searchedTabs } = search;
    const sort = tabData.sort || {};

    // re-render conditions
    const searchPerformed = searchedTabs.includes(tab);
    const shouldSort = !sort.sorted;
    const sortShouldReset = sort.sorted && !this.isDefaultSort(sort.sortColumn, sort.sortDirection);
    const shouldSearch = (!searchPerformed && keyword) || prevKeyword !== keyword;
    const searchShouldReset = searchPerformed && !keyword;

    const isSyscoSupported = syscoBrandMapping[tab];
    let isSyscoDominant = false;
    if (isSyscoSupported) {
      const products = tabData.products;
      isSyscoDominant = products && products.length && products.some(product => product.isSyscoBrand);
    }
    // when tab change, the next tab is not sorted yet
    // same tab, but not yet sorted
    if (shouldSort || tabChanged && sortShouldReset) {
      updateSortProps(tab, {
        sorted: true,
        sortDirection: isSyscoDominant ? defaultSorting.primaryDir : defaultSorting.secondaryDir,
        sortColumn: isSyscoDominant ? defaultSorting.primaryCol : defaultSorting.secondaryCol,
      });
      this.filterAndUpdate(tab, tabData, filters, defaultSorting.primaryCol, defaultSorting.primaryDir, search, unitTypes, isSyscoDominant);
    }
    // same tab and the products/ filters changed
    // search should be cleared
    else if ((!tabChanged && shouldUpdate && !shouldSort) || searchShouldReset || (tabChanged && shouldSearch)) {
      this.filterAndUpdate(tab, tabData, filters, sort.sortColumn, sort.sortDirection, search, unitTypes, isSyscoDominant);
    }
    // when the same tab is searched consecutively, other tabs should be removed from searched list
    else if (!tabChanged && shouldSearch) {
      this.filterAndUpdate(tab, tabData, filters, sort.sortColumn, sort.sortDirection, {
        ...search,
        searchedTabs: [tab],
      }, unitTypes, isSyscoDominant);
    }
  }

  /**
   * Generate table data, apply search and filters and change state
   * @param {String} tabKey tabKey name
   * @param {Object} tabData tab data from store
   * @param {Object} filters filters from store
   * @param {String} sortColumn sort Column
   * @param {String} sortDirection sort direction
   * @param {Object} searchProps search properties
   * @param {*} unitTypes
   * @param {Boolean} isSyscoDominant sorting should dominate the sysco products
   */
  filterAndUpdate = (tabKey, tabData, filters, sortColumn, sortDirection, searchProps, unitTypes, isSyscoDominant) => {
    const { isBatchRecipe, recipeId } = this.props;
    const { keyword, searchedTabs } = searchProps;
    const products = tabData.products || [];
    const searchPerformed = searchedTabs ? searchedTabs.includes(tabKey) : false;
    let searchedTabsNew = null;

    // search should be cleared to the tab
    if (searchPerformed && !keyword) {
      searchedTabsNew = searchedTabs.filter(tab => tab !== tabKey);
    }
    // search should be performed to the tab
    else if (keyword) {
      searchedTabsNew = searchPerformed ? searchedTabs : [...searchedTabs, tabKey];
    }

    let tableData = generateTableData(products, filters, tabKey, searchProps, unitTypes, isBatchRecipe, recipeId);

    const intendedSortConfig = {
      [COLUMN_KEY]: sortColumn,
      [DIRECTION_KEY]: sortDirection.toLocaleLowerCase(),
    };
    const secondarySortConfig = {
      [COLUMN_KEY]: defaultSorting.secondaryCol,
      [DIRECTION_KEY]: defaultSorting.secondaryDir,
    };

    let primarySort, secondarySort;
    if (isSyscoDominant) {
      primarySort = intendedSortConfig;
      secondarySort = secondarySortConfig;
    } else {
      primarySort = secondarySortConfig;
      secondarySort = null;
    }

    tableData = ListUtilities.sortByColumn(tableData, primarySort, secondarySort);

    if (searchedTabsNew) {
      this.props.updateSearchedTabs(searchedTabsNew);
      this.setState({
        [tabKey]: tableData,
        currentSearchKeyword: keyword || '',
      });
    } else {
      this.setState({
        [tabKey]: tableData,
      });
    }
  }

  onHeaderClick({ dataKey }) {
    if (dataKey === selectColumnId) {
      return;
    }

    // prev values
    const { selectedTab, ingredientsSearch, updateSortProps } = this.props;
    const { sortColumn, sortDirection } = ingredientsSearch[selectedTab].sort;
    let nextSortDirection;
    // swap direction on 2nd click
    if (sortColumn === dataKey) {
      if (sortDirection === SortDirection.DESC)
        nextSortDirection = SortDirection.ASC;
      else
        nextSortDirection = SortDirection.DESC;
    } else {
      nextSortDirection = SortDirection.ASC;
    }

    //sorting
    const sortData = ListUtilities.sortByColumn(this.state[selectedTab], {
      [COLUMN_KEY]: dataKey,
      [DIRECTION_KEY]: nextSortDirection.toLocaleLowerCase(),
    }, {
      [COLUMN_KEY]: defaultSorting.secondaryCol,
      [DIRECTION_KEY]: defaultSorting.secondaryDir,
    });

    // update data and params
    this.setState({
      [selectedTab]: sortData,
      focusOnRow: 0,
    });
    updateSortProps(selectedTab, {
      sorted: true,
      sortDirection: nextSortDirection,
      sortColumn: dataKey,
    });
  }

  /**
   * Handles tab change. Handles product catalog data fetches specifically
   * @param {Object} tab tab object
   * @param {Number} index tab index
   */
  handleTabChange = tab => {
    const productCatTabName = ingredientsSearchTabs.FULL_PRODUCT_CATALOG_TAB.key;
    const {
      changeSelectedTab,
      fetchProductCatalogue,
      resetTab,
      ingredientsSearch,
      selectedFilters,
      unitTypes,
      isBatchRecipe,
      recipeId,
    } = this.props;

    // handle product catalogue fetches
    if (tab.key === productCatTabName) {
      const { search } = ingredientsSearch;
      const productCatTab = ingredientsSearch[productCatTabName] || {};
      const productCatProducts = productCatTab.products || [];
      const productCatTabShouldClear = productCatProducts.length > 0 && !search.keyword;
      if (!search.searchedTabs.includes(productCatTabName) && search.keyword) {
        fetchProductCatalogue(search.keyword);
      } else if (productCatTabShouldClear) {
        resetTab(productCatTabName);
      }
    }
    changeSelectedTab(tab.key);

    if (ingredientsSearch[tab.key].loaded && this.state[tab.key].length === 0) {
      const products = ingredientsSearch[tab.key].products || [];
      const tableData = generateTableData(products, selectedFilters, tab.key, ingredientsSearch.search, unitTypes, isBatchRecipe, recipeId);
      this.setState({ [tab.key]: tableData });
    }
  };

  /**
   * Returns whether the sorting configuration is default
   * @param {String} column sorted column
   * @param {String} direction sorted direction
   * @returns {Boolean} is sorting configuration default
   */
  isDefaultSort = (column, direction) => column === defaultSorting.primaryCol && direction === defaultSorting.primaryDir;

  /**
   * render add new ingredient button
   * @param {Object} customIngredients custom ingredient tab object
   */
  renderAddNewCustomIngredient = (selectedTab, rowToFocus) => {
    const { isIngredientReplacement } = this.props;
    if (selectedTab === ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key) {
      return (
        <div className={'fixedbar-ing-search'}>
          {
            this.state.showEditableRow ?
              <AddIngredient
                cancelAddingNewIngredient={() => this.setState({ showEditableRow: false })}
                onNewCustomIngredientSave={this.onNewCustomIngredientSave}
                commonCustomUom={this.props.commonCustomUom}
                rowToFocus={rowToFocus}
              /> : (
                (!isIngredientReplacement) ? <Link
                  onClick={() => this.setState({ showEditableRow: true })}
                  className='pointer'
                >
                  Add a New Custom Ingredient
                </Link> : null)
          }
        </div>
      )
    }
    return null;
  }

  /**
   * Returns the row index to be focused
   * @param {Object} customIngredients custom ingredient tab object
   * @returns {Number} the row to be focused on
   */
  getRowToFocus = (customIngredients = {}) => {
    const { selectedTab } = this.props;
    const products = customIngredients.products || [];
    const newIngredients = customIngredients.newIngredients || [];
    if (selectedTab === ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key && newIngredients.length) {
      return products.length + newIngredients.length - 1;
    }
    return this.state.focusOnRow;
  }

  /**
   * save ingredient wrapper for revert back the add ingredient button
   * @param {Object} ingredient ingredient object
   */
  onNewCustomIngredientSave = ingredient => {
    this.props.onNewCustomIngredientSave(ingredient);
    this.setState({ showEditableRow: false });
  }

  componentWillUnmount() {
    const {
      updateSortProps,
      selectedTab,
    } = this.props;

    updateSortProps(selectedTab, {
      sorted: false,
      sortDirection: null,
      sortColumn: null,
    });
  }

  render() {
    const {
      selectedTab,
      ingredientsSearch,
      loading,
      onIngredientSelect,
      isIngredientReplacement,
      selectedKey,
      isFromNotificationCenter,
      ingredientReplacement
    } = this.props;
    const {
      tabHeaders,
      tabKeys,
    } = this.state;

    const tabData = ingredientsSearch[selectedTab] || {};
    const sort = tabData.sort;
    const { sortColumn, sortDirection } = sort ? sort : {};
    const data = selectedTab ? this.state[selectedTab] : [];
    let customIngredients = {};
    let newIngredients = [];
    let showNoListsMsg = null;
    if (selectedTab === ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key) {
      customIngredients = tabData;
      newIngredients = customIngredients.newIngredients || [];
    }

    if (selectedTab === ingredientsSearchTabs.ITEM_LISTS_TAB.key && Array.isArray(tabData.facets)
      && tabData.facets.length > 0) {  // checking facets instead of products to capture empty list scenario

      const listFacet = tabData.facets.find(facet => facet.id === ingredientsSearchFilters.LIST_NAME.id);
      showNoListsMsg = (listFacet && Array.isArray(listFacet.values) && listFacet.values.length === 0);
    }

    const rowToFocus = this.getRowToFocus(customIngredients);
    const tabIndex = tabKeys.indexOf(selectedTab);

    return (
      <div className={'tab-wrapper'} onKeyDown={preventTabDefault} tabIndex={-1}>
        <Tabs
          tabs={tabHeaders}
          useOnPan={true}
          page={tabIndex < 0 ? 0 : tabIndex}
          onChange={tab => this.handleTabChange(tab)}
          renderTabBar={
            props =>
              <div id={ingredientTabId}>
                <TabBar
                  renderTab={tab => <div id={tab.key}>{tab.title}</div>}
                  {...props}
                />
              </div>
          }
        >
          <div
            className={selectedTab === ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key ? 'with-bottom-bar' : 'without-bottom-bar'}
          >
            {
              !loading ?
                <IngredientTable
                  data={[...data, ...newIngredients]}
                  onHeaderClick={this.onHeaderClick.bind(this)}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  focusOnRow={rowToFocus}
                  resetReposition={() => this.setState({ focusOnRow: null })}
                  onSelect={selectedIngredient => onIngredientSelect(selectedIngredient, selectedTab)}
                  selectedTab={selectedTab}
                  search={ingredientsSearch.search || {}}
                  showNoListsMsg={ showNoListsMsg }
                  isIngredientReplacement={ isIngredientReplacement }
                  selectedKey={ selectedKey }
                  isFromNotificationCenter={ isFromNotificationCenter }
                  ingredientReplacement={ ingredientReplacement }
                /> : <PageLoadingCircle open />
            }
            {this.renderAddNewCustomIngredient(selectedTab, rowToFocus)}
          </div>
        </Tabs>
      </div>
    );
  }
}

IngredientsSearchResults.propTypes = {
  changeSelectedTab: PropTypes.func,
  selectedTab: PropTypes.string,
  ingredientsSearch: PropTypes.object,
  selectedFilters: PropTypes.object,
  onIngredientSelect: PropTypes.func,
  unitTypes: PropTypes.object,
  isBatchRecipe: PropTypes.bool,
  recipeId: PropTypes.number,
  loading: PropTypes.bool,
  showEditableCustomIngRow: PropTypes.func,
  onNewCustomIngredientSave: PropTypes.func,
  cancelAddingNewIngredient: PropTypes.func,
  commonCustomUom: PropTypes.object,
  userCountry: PropTypes.string,
  isIngredientReplacement: PropTypes.bool,
  selectedKey: PropTypes.string,
  isFromNotificationCenter: PropTypes.bool,
  ingredientReplacement: PropTypes.object,
};

export default IngredientsSearchResults;
